/**
 * Fail-closed route guard (plan §6.8.11).
 * Local bypass: VITE_DATA_SOURCE=local + VITE_AUTH_MODE=disabled.
 * Caches only deterministic verdicts; return-to is path + validated search.
 */
import { useEffect, type JSX, type ReactNode } from 'react';

import type { AppConfig } from '@/config';

import { useAuth, type AuthStatus } from './auth-context';
import { isLocalAuthDisabled } from './local-bypass';
import { resolveReturnTo, type ReturnToLocation } from './return-to';
import styles from './route-guard.module.css';

export interface LoginRedirectTarget {
  readonly pathname: string;
  readonly returnTo?: ReturnToLocation;
}

export interface RouteGuardProps {
  readonly config: AppConfig;
  readonly children: ReactNode;
  readonly returnTo?: ReturnToLocation;
  readonly onRedirectToLogin?: (target: LoginRedirectTarget) => void;
}

export function RouteGuard({
  config,
  children,
  returnTo,
  onRedirectToLogin,
}: RouteGuardProps): JSX.Element | null {
  const auth = useAuth();
  const bypass = isLocalAuthDisabled(config);

  useEffect(() => {
    if (bypass) return;
    if (auth.status !== 'unauthenticated') return;
    const safe = resolveReturnTo(returnTo);
    onRedirectToLogin?.({
      pathname: '/login',
      returnTo: safe,
    });
  }, [auth.status, bypass, onRedirectToLogin, returnTo]);

  /*
   * The guard's EFFECTIVE status: the local bypass is the same config predicate
   * the provider echoes as 'bypassed', so folding it in here rather than
   * short-circuiting above keeps one decision point instead of two.
   *
   * It also keeps the switch honest. Handling bypass as an early return left
   * `case 'bypassed'` reachable only when the guard and the provider disagree
   * about the config — a state no correct wiring produces, so the label sat
   * permanently unexecuted and dragged branch coverage down. Mapped this way,
   * that label is taken on every bypassed render and covered by the existing
   * bypass cases, while still absorbing the disagreement if it ever occurs.
   */
  const status: AuthStatus = bypass ? 'bypassed' : auth.status;

  /*
   * An EXHAUSTIVE switch, deliberately with no `default`.
   *
   * `AuthStatus` is defined in another module, so nothing local guarantees this
   * covers it — and the failure mode of missing a member is silent: the old
   * if-chain fell through to `return null`, rendering a blank page with no
   * redirect, no retry and no error. That is the exact dead end 'unavailable'
   * was introduced to remove, and it would have come back unnoticed.
   *
   * Omitting `default` is what makes it a COMPILE error rather than a runtime
   * one. Every arm returns, so a new member leaves a path that falls out of the
   * switch, and `noImplicitReturns` (tsconfig.base.json) rejects the function.
   * A `default: assertNever(status)` would give the same compile-time guarantee
   * but add a branch that can never execute — permanently unexecuted coverage,
   * which style §3 asks us not to write when we have this alternative.
   *
   * Keep every arm returning. An arm that falls through re-opens the hole.
   */
  switch (status) {
    case 'bypassed':
    case 'authenticated':
      return <>{children}</>;

    case 'idle':
    case 'loading':
      return (
        <div role="status" aria-live="polite">
          Checking session…
        </div>
      );

    /*
     * The probe could not be evaluated and there was no session to retain. NOT
     * a redirect: we do not know that the user is signed out, and sending them
     * to /login would discard the deep link and put them in front of a form
     * whose endpoint is equally unreachable. `role="alert"` rather than
     * `role="status"` because this is terminal and actionable, not progress.
     */
    case 'unavailable':
      return (
        <div className={styles['root']}>
          <p className={styles['message']} role="alert">
            Could not check your session. This is a connection problem, not a sign-out.
          </p>
          <button type="button" className={styles['retry']} onClick={() => void auth.refresh()}>
            Try again
          </button>
        </div>
      );

    /* The redirect effect above owns navigation; render nothing sensitive. */
    case 'unauthenticated':
      return null;
  }
}
